import { useEffect, useState } from 'react';
import { api, type Brand, type HookVariant } from '../lib/api';

const TONE: Record<HookVariant['verdict'], string> = {
  strong: 'var(--green)', testing: 'var(--amber)', weak: 'var(--red)',
};

/**
 * Hooks, and a way out of them.
 *
 * This screen generated six scored opening lines and then stopped. There was no
 * button connecting it to anything — Thomas generated a hook, then retyped it
 * by hand into a new card, which is the whole job this screen exists to remove.
 *
 * POST /cards has always accepted topic and painPoint. The board sent neither,
 * so the reasoning behind a card — the problem it names and the subject it sits
 * in — was thrown away at the moment it was worked out.
 */
export function Hooks({ brand, onOpenCard, seed }: { brand: Brand | null; onOpenCard?: (id: string) => void;
  /** D16: a theme and a subject to start from — the next episode from what worked. */
  seed?: { topic?: string | null; pain?: string | null } | null }) {
  const [pain, setPain] = useState(
    'A ringing phone at an auto shop competes with mechanics under a lift and a busy service writer, so it goes to voicemail — and the caller just dials the next shop.',
  );
  const [topic, setTopic] = useState('Automotive');
  useEffect(() => {
    if (!seed) return;
    if (seed.topic) setTopic(seed.topic);
    if (seed.pain) setPain(seed.pain);
  }, [seed]);
  const [variants, setVariants] = useState<HookVariant[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<string | null>(null);
  const [making, setMaking] = useState<string | null>(null);
  // "Produce with the assistant": the card, the script, the arrangement —
  // scenes, joins, footage, banners, the bed — in one go, every proposal
  // applied, so what opens is a produced piece to adjust rather than a
  // blank card to fill. Said step by step while it runs, because it takes a
  // little while and silence reads as a hang.
  const [producing, setProducing] = useState<{ line: string; step: string } | null>(null);
  const [length, setLength] = useState(45);
  // D12: what worked — the published hooks with the most views, the next brief's start.
  const [worked, setWorked] = useState<Array<{ card_id: string; title: string; topic: string | null; pain_point: string | null; views: number; platform: string }>>([]);
  useEffect(() => { api.hooksWorked(brand?.id ?? null).then(setWorked).catch(() => undefined); }, [brand?.id]);

  // The hook becomes the card's title, and the pain point and topic travel with
  // it. The card then arrives in Idea already knowing what it is about, which
  // is what the script writer reads.
  async function startCard(line: string) {
    setMaking(line); setError(null); setMade(null);
    try {
      await api.createCard({
        title: line,
        brandId: brand?.id ?? null,
        topic: topic.trim() || undefined,
        painPoint: pain.trim() || undefined,
      });
      setMade(line);
    } catch (e) { setError((e as Error).message); }
    finally { setMaking(null); }
  }

  async function produce(line: string) {
    setProducing({ line, step: 'creating the card' }); setError(null); setMade(null);
    try {
      const card = await api.createCard({
        title: line, brandId: brand?.id ?? null,
        topic: topic.trim() || undefined, painPoint: pain.trim() || undefined,
      });
      setProducing({ line, step: 'writing the script, gathering this episode\u2019s footage, dressing and arranging it, setting the cover' });
      // The brand's cast, so the piece is voiced by the people it has rather
      // than by a generic host. The card's own defaults win when they say.
      const cast = Object.keys(brand?.voices ?? {}).slice(0, 5);
      // 190: the whole one shot, server-side — every proposal applied, the
      // cover set (the cast's faces when the show has them, else the opening
      // frame, under the hook with its strongest word in the accent), and the
      // card landed in Scripted with the cut laid out to tweak. One request,
      // so it survives this tab.
      await api.produce(card.id, { targetSeconds: length, ...(cast.length ? { speakers: cast } : {}), approve: true, thumbnail: true, land: 'scripted' });
      setProducing(null);
      onOpenCard?.(card.id);
    } catch (e) { setError((e as Error).message); setProducing(null); }
  }

  async function run() {
    setBusy(true); setError(null);
    // 190: the show and the length go with it — its own hooks that worked, its language, a hook sized to the piece.
    try { setVariants(await api.hooks({ painPoint: pain, topic, brandId: brand?.id ?? null, targetSeconds: length })); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="vhead">
        <div>
          <h2>Hooks &amp; SEO</h2>
          <p>
            A theme and a subject. Every hook must name the subject — a concrete pain
            point — in the first fifteen seconds; generic framing scores itself out.
          </p>
        </div>
        <button className="btn pri" onClick={() => void run()} disabled={busy || pain.trim().length < 10}>
          {busy ? 'Generating…' : 'Generate variants'}
        </button>
      </div>

      {worked.length > 0 && (
        <div className="notice" data-tool="worked">
          <b>What worked.</b> The hooks this show published that earned the most views — the generator is shown them and asked for the shape, not the words.
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {worked.map((w) => (
              <li key={w.card_id}>
                <button type="button" className="chip" title="Start from this theme and subject"
                        onClick={() => { if (w.topic) setTopic(w.topic); if (w.pain_point) setPain(w.pain_point); }}>
                  {w.views.toLocaleString()} views
                </button>{' '}
                {w.title}
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <div className="err">{error}</div>}
      {made && (
        <div className="notice">
          Sent to the <b>Flight Deck</b> — “{made.slice(0, 70)}{made.length > 70 ? '…' : ''}” is in Idea, ready to work by hand.
        </div>
      )}
      {producing && (
        <div className="notice">
          Producing “{producing.line.slice(0, 60)}{producing.line.length > 60 ? '…' : ''}” — {producing.step}…
        </div>
      )}

      <div className="panel">
        <div className="panel-hd"><h3>Theme and subject</h3><span className="chip">{topic || 'no theme'}</span></div>
        <div className="panel-bd kit-form">
          <label className="field">
            Theme
            <input value={topic} onChange={(e) => setTopic(e.target.value)}
                   placeholder="The recurring theme — e.g. Home vegetable gardening" />
          </label>
          <label className="field">
            Assistant length
            <select value={length} onChange={(e) => setLength(Number(e.target.value))}>
              <option value={45}>45s</option><option value={90}>90s</option>
              <option value={180}>3 min</option><option value={600}>10 min</option>
            </select>
          </label>
          <label className="field" style={{ gridColumn: '1 / -1' }}>
            Subject — the pain point
            <textarea value={pain} onChange={(e) => setPain(e.target.value)} rows={3}
                      placeholder="The problem, in one concrete sentence — before the product." />
          </label>
        </div>
      </div>

      {variants && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-hd">
            <h3>Variants</h3><span className="chip">scored on retention risk</span>
          </div>
          <table>
            <thead><tr><th>Opening line</th><th>Angle</th><th className="r">Hold</th><th>Why</th><th /></tr></thead>
            <tbody>
              {variants.map((v, i) => (
                <tr key={i}>
                  <td><b>{v.line}</b></td>
                  <td style={{ color: 'var(--ink-2)' }}>{v.angle}</td>
                  <td className="r">
                    <span style={{ color: TONE[v.verdict], fontWeight: 700 }}>{v.hold}</span>
                  </td>
                  <td style={{ color: 'var(--ink-2)', fontSize: 12.5 }}>{v.why}</td>
                  <td className="r">
                    <span className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                      <button
                        className="btn"
                        disabled={making !== null || producing !== null}
                        title="A card in Idea, to script and arrange yourself"
                        onClick={() => void startCard(v.line)}
                      >
                        {making === v.line ? 'Sending…' : 'Send to Flight Deck'}
                      </button>
                      <button
                        className="btn pri"
                        disabled={making !== null || producing !== null}
                        title="The script, the scenes, the joins, the footage, the banners and the bed — produced from this hook and opened for you to adjust"
                        onClick={() => void produce(v.line)}
                      >
                        {producing?.line === v.line ? 'Producing…' : 'Produce with the assistant'}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
